import React, { useState, useEffect } from 'react';
import { PlanDetail, CheckoutConfig } from '../types';
import { CURRENCIES } from '../constants';
import { useSettings } from './SettingsContext';
import { api } from '../services/api';
import CheckoutModal from './CheckoutModal';
import { applyABTests, getAssignedVariation } from '../utils/abTesting';
import { trackPageView, trackCheckoutInitiated } from '../services/analyticsService';
import { TemplateDispatcher } from './checkout/templates/TemplateDispatcher';

interface HostedCheckoutProps {
  plan: PlanDetail;
  onClose: () => void;
  configOverride?: CheckoutConfig;
  initialFormDataOverride?: Record<string, string>;
  initialBillingCycle?: 'monthly' | 'quarterly' | 'annually';
  autoStart?: boolean;
}

const readCheckoutFormDataFromUrl = (): Record<string, string> => {
  let search = window.location.search;
  if (!search && window.location.hash.includes('?')) {
    search = window.location.hash.substring(window.location.hash.indexOf('?'));
  }

  const params = new URLSearchParams(search);
  const initial: Record<string, string> = {};
  ['email', 'customer_email', 'name', 'customer_name', 'phone', 'customer_phone', 'country', 'gstin'].forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) initial[key.replace('customer_', '')] = value;
  });
  return initial;
};

const mergeCheckoutFormData = (...sources: Array<Record<string, string> | undefined>): Record<string, string> => {
  return sources.reduce<Record<string, string>>((acc, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim()) acc[key] = value.trim();
    });
    return acc;
  }, {});
};

const HostedCheckout: React.FC<HostedCheckoutProps> = ({
  plan,
  onClose,
  configOverride,
  initialFormDataOverride,
  initialBillingCycle,
  autoStart = false,
}) => {
  const { settings, updateSettings } = useSettings();
  const config = configOverride || applyABTests(settings.checkoutConfig);

  const [step, setStep] = useState<'info' | 'payment'>('info');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'annually'>(() => initialBillingCycle || 'monthly');
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const [formData, setFormData] = useState<Record<string, string>>(() =>
    mergeCheckoutFormData(readCheckoutFormDataFromUrl(), initialFormDataOverride)
  );

  useEffect(() => {
    if (initialFormDataOverride) {
      setFormData((prev) => mergeCheckoutFormData(readCheckoutFormDataFromUrl(), prev, initialFormDataOverride));
    }
  }, [initialFormDataOverride]);

  useEffect(() => {
    trackPageView(`Hosted Checkout - ${plan.name || plan.tier}`, '/checkout');
    if (!configOverride && settings.checkoutConfig?.abTests) {
      settings.checkoutConfig.abTests.forEach((test) => {
        if (test.isEnabled) {
          const varId = getAssignedVariation(test);
          if (varId) {
            const updated = settings.checkoutConfig.abTests.map((t) =>
              t.id === test.id
                ? {
                    ...t,
                    variations: t.variations.map((v) =>
                      v.id === varId ? { ...v, stats: { views: (v.stats?.views || 0) + 1, conversions: v.stats?.conversions || 0 } } : v
                    ),
                  }
                : t
            );
            updateSettings({ checkoutConfig: { ...settings.checkoutConfig, abTests: updated } });
          }
        }
      });
    }
  }, []);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleToggleAddon = (id: string) => {
    setSelectedAddonIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  // Pricing calculations
  const currencySymbol = CURRENCIES.find((c) => c.code === plan.currency)?.symbol || '₹';
  const basePrice = plan.price || 0;
  const activeAddons = (config.addons || []).filter((a) => a.isEnabled && selectedAddonIds.includes(a.id));
  const addonsTotal = activeAddons.reduce((sum, a) => sum + (a.price || 0), 0);

  let cycleDiscountPercent = 0;
  if (billingCycle === 'quarterly') cycleDiscountPercent = config.quarterlyDiscountPercentage || 10;
  if (billingCycle === 'annually') cycleDiscountPercent = config.yearlyDiscountPercentage || 20;

  const couponDiscountPercent = appliedCoupon ? config.couponDiscountPercentage || 15 : 0;
  const totalDiscountPercent = Math.min(cycleDiscountPercent + couponDiscountPercent, 90);

  const subtotal = basePrice + addonsTotal;
  const discountAmount = subtotal * (totalDiscountPercent / 100);
  const finalTotal = Math.max(0, subtotal - discountAmount);

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCapturing(true);

    const captureData = {
      email: formData.email || '',
      customerName: formData.name || '',
      planTier: plan.tier,
      planPrice: finalTotal,
      currency: plan.currency || 'INR',
      status: 'Captured' as const,
      abandonedAt: new Date().toISOString(),
      lastStep: 'Add Payment Info',
    };

    try {
      const tracked = await api.abandonment.track(captureData);
      if (tracked?.id) localStorage.setItem('ss_last_checkout_prospect_id', tracked.id);
      trackCheckoutInitiated(plan.name || plan.tier, finalTotal, plan.currency || 'INR');
      setStep('payment');
    } catch {
      setStep('payment');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSuccess = () => {
    onClose();
  };

  if (step === 'payment') {
    return (
      <div className="min-h-full flex items-center justify-center p-4 md:p-8 bg-slate-900/40 backdrop-blur-sm">
        <div className="w-full max-w-xl animate-in zoom-in-95">
          <CheckoutModal
            plan={{ ...plan, price: finalTotal }}
            billingCycle={billingCycle}
            onClose={() => setStep('info')}
            onSuccess={handleSuccess}
            customerDataOverride={{
              name: formData.name || '',
              email: formData.email || '',
              phone: formData.phone || '',
              gstin: formData.gstin || '',
              country: formData.country || '',
            }}
            autoStart
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-full">
      <TemplateDispatcher
        plan={plan}
        config={config}
        formData={formData}
        onInputChange={handleInputChange}
        billingCycle={billingCycle}
        onChangeCycle={setBillingCycle}
        selectedAddonIds={selectedAddonIds}
        onToggleAddon={handleToggleAddon}
        appliedCoupon={appliedCoupon}
        onApplyCoupon={setAppliedCoupon}
        onRemoveCoupon={() => setAppliedCoupon(null)}
        onSubmitInfo={handleInfoSubmit}
        isCapturing={isCapturing}
        currencySymbol={currencySymbol}
        addonsTotal={addonsTotal}
        discountAmount={discountAmount}
        finalTotal={finalTotal}
      />
    </div>
  );
};

export default HostedCheckout;
